// Tipos del perfil de impresora Web Bluetooth (BLE).
//
// Vive en un archivo propio (no en `@interfaces/general`, que es compartido) para
// que el integrador decida si lo centraliza. El perfil es propiedad del EQUIPO
// físico (per-dispositivo): persiste en localStorage y sobrevive login/logout del
// cajero, igual que la config de impresoras Electron.
//
// `deviceId` es VOLÁTIL entre sesiones de navegador (Web Bluetooth re-asigna el id
// al reconectar). Se persiste como pista para `getDevices()`, pero el flujo de
// reconexión del spike también hace match por `deviceName`.

/** Tipo de ticket a imprimir: completo (normal) o de cocina (simplified). */
export type WebBleTicketType = 'normal' | 'simplified';

/**
 * Tamaño de texto del ticket BLE. Los 5 niveles mapean 1:1 con las keys de
 * `PRINT_TEXT_SIZE_MAP` y `PRINT_TEXT_SIZES_VALUES_MAP` (src/utils/constants.ts):
 *   xxs → 'Muy pequeño' · small → 'Pequeño' · medium → 'Mediano' ·
 *   large → 'Grande' · xxl → 'Muy grande'.
 * El renderer a canvas usa `PRINT_TEXT_SIZES_VALUES_MAP[textSize]` (px por rol)
 * escalado al ancho en puntos. El fallback de texto (buildWebBleTicket → titleSize)
 * colapsa xxs→pequeño y xxl→grande porque la térmica no tiene escala de px.
 * Perfiles persistidos con 'small'|'medium'|'large' siguen siendo válidos (subset).
 */
export type WebBleTextSize = 'xxs' | 'small' | 'medium' | 'large' | 'xxl';

/** Ancho físico del rollo en milímetros (define caracteres por línea del ticket). */
export type WebBlePageWidth = 58 | 80;

/**
 * Política de auto-impresión de pedidos entrantes:
 * - `disabled`: nunca auto-imprime (solo botón manual).
 * - `all`: imprime todo pedido entrante.
 * - `confirmed`: imprime solo al confirmarse el pedido.
 */
export type WebBleAutoPrint = 'disabled' | 'all' | 'confirmed';

/** Perfil persistido de una impresora térmica Web Bluetooth. */
export interface WebBlePrintSettings {
  /** Id del dispositivo Web Bluetooth. Volátil entre sesiones de navegador. */
  deviceId: string | null;
  /** Nombre anunciado del dispositivo BLE (usado para reconexión/chooser filtrado). */
  deviceName: string | null;
  /** Etiqueta editable por el usuario para identificar la impresora en la UI. */
  label: string;
  /** Opt-in: el camino BLE solo se activa si la impresora está habilitada. */
  enabled: boolean;
  /**
   * Formato de impresión:
   * - 'text'  → ticket de texto (rápido, usa la fuente de la impresora).
   * - 'image' → ticket renderizado como imagen (se ve igual a la app; más lento).
   * Default 'text' por velocidad/compatibilidad; 'image' es opt-in por impresora.
   */
  ticketFormat: 'text' | 'image';
  /** Imprimir el logo del comercio (raster) al inicio del ticket. */
  showLogo: boolean;
  ticketType: WebBleTicketType;
  textSize: WebBleTextSize;
  /** Ancho del rollo en mm (58 ó 80) — define el ancho de línea del ticket. */
  pageWidth: WebBlePageWidth;
  /** Líneas separadoras y bordes decorativos en el ticket. */
  decoration: boolean;
  /** Número de copias por impresión (≥1). */
  copies: number;
  autoPrint: WebBleAutoPrint;
  /** Imprimir el celular del cliente ("Celular: ..."). Default true. Inerte en cocina. */
  showClientPhone: boolean;
  /**
   * Imprimir el NOMBRE del comercio. Default true. Aplica a AMBOS roles (caja y
   * comanda de cocina) — identifica el origen del pedido en cocinas multi-marca.
   */
  showCommerceName: boolean;
  /**
   * Imprimir la DIRECCIÓN del comercio en el footer. Default true. Solo aplica a
   * caja; la comanda de cocina nunca imprime dirección (toggle inerte en cocina).
   */
  showCommerceAddress: boolean;

  // ───────────────────────────────────────────────────────────────────────────
  // Contenido RICO configurable (TicketContentFlags). NUEVO en la unificación del
  // builder. Son OPCIONALES en el tipo persistido a propósito: los perfiles ya
  // guardados en localStorage (que solo traían `ticketType`) siguen siendo válidos
  // y se HIDRATAN PEREZOSAMENTE desde el preset del rol (CAJA_PRESET/COCINA_PRESET)
  // vía `hydrateContentFlags` al leerlos — sin reescribir la llave durable
  // `webBlePrintersConfig` (que resetStore(login) NO debe tocar, patrón PR#1020).
  //
  // `undefined` (campo ausente) ≡ "usar el default del preset del rol". Una vez que
  // el usuario toca un toggle, el campo queda `true|false` explícito y persiste.
  // ───────────────────────────────────────────────────────────────────────────

  /** Mostrar el NOMBRE del cliente ("Cliente:"). Default: caja=on, cocina=off. */
  showClientName?: boolean;
  /** Mostrar PRECIOS por producto/extra (caja). Default: caja=on, cocina=off. */
  showPrices?: boolean;
  /** Mostrar el bloque de TOTALES (subtotal/total). Default: caja=on, cocina=off. */
  showTotals?: boolean;
  /** Mostrar la PROPINA dentro de los totales. Default: caja=on, cocina=off. Inerte si showTotals=off. */
  showTip?: boolean;
  /** Mostrar el DOMICILIO de entrega (solo delivery). Default: caja=on, cocina=off. */
  showDeliveryAddress?: boolean;
  /**
   * Mostrar el REPARTIDOR asignado (nombre + teléfono). Default: OFF en AMBOS presets — el
   * repartidor se asigna DESPUÉS de que el pedido entra, así que el ticket de auto-impresión
   * nunca lo trae; solo aparece al REIMPRIMIR. Se deja opt-in para no cambiar el papel de
   * nadie sin pedirlo. Inerte si el pedido no es delivery o no tiene repartidor asignado.
   */
  showDeliveryDriver?: boolean;
  /** Mostrar el COMENTARIO/indicaciones del pedido. Default: ambos on. */
  showComment?: boolean;
  /** Mostrar la FECHA y hora al pie. Default: ambos on. */
  showDate?: boolean;
  /** Mostrar el aviso legal al pie (solo caja). Default: caja=on, cocina=off. */
  showLegalDisclaimer?: boolean;
  /** Mostrar el PRECIO de los extras (caja). Default sigue a showPrices. Inerte si showPrices=off. */
  extrasShowPrice?: boolean;
  /**
   * Velocidad de impresión: paso del slider (0 = más compatible/seguro … mayor = más rápido).
   * Mapea a un par (chunk, respiro) vía resolveSpeedStep (bleTransport). Es config DEL DISPOSITIVO
   * (cada modelo topa distinto), persistida en la llave durable → sobrevive desconexión y logout.
   * undefined → paso 0 (default seguro). NO entra en webBlePrintJobSignature: cambia el TIEMPO de
   * envío, no los BYTES del papel → no afecta la dedup de auto-print.
   */
  speedStep?: number;
  /**
   * Modo fluido: fuerza la ruta de escritura CON confirmación de la impresora (ACK). Cuando la
   * impresora confirma cada envío, ella marca el ritmo y el ticket sale parejo — por eso el
   * auto-tuning lo activa ante "salió a tirones" (regla d de `autoTune.ts`).
   * `undefined` (o `false`) = comportamiento histórico: el transporte deriva el modo de las
   * propiedades de la característica. `true` = forzar ACK (solo tiene efecto si la característica
   * expone `writeValue`; si no, el transporte conserva la ruta sin confirmación).
   * Igual que `speedStep`, NO entra en `webBlePrintJobSignature`: cambia CÓMO se envían los
   * bytes, no QUÉ bytes salen → no debe partir la dedup de auto-print (doble impresión, PR #1020).
   */
  bleWriteWithResponse?: boolean;
}

/** Estado del slice: mapa dinámico keyed por id de perfil (no por deviceId). */
export type WebBlePrintersState = Record<string, WebBlePrintSettings>;

// ─────────────────────────────────────────────────────────────────────────────
// TicketContentFlags — subconjunto SEMÁNTICO de WebBlePrintSettings que decide QUÉ
// contenido lleva el ticket. NAMING canónico (documentado): cada flag dice qué
// SECCIÓN se imprime, no cómo. Es la única superficie de configurabilidad de
// contenido; caja/cocina son PRESETS que la inicializan (no roles que ramifican).
//
//   showLogo · showCommerceName · showCommerceAddress · showClientName ·
//   showClientPhone · showPrices · showTotals · showTip · showDeliveryAddress ·
//   showDeliveryDriver · showComment · showDate · showLegalDisclaimer ·
//   extrasShowPrice · title
//
// Estos flags se RESUELVEN (con sus defaults por preset) por `resolveContentFlags`
// y los consume el IR (buildTicketModel). Cada uno tiene un valor concreto aquí
// (no opcional): la resolución ya aplicó el default del preset.
// ─────────────────────────────────────────────────────────────────────────────
export interface TicketContentFlags {
  /** Título del ticket ('Ticket pedido' / 'Ticket de Cocina'). */
  title: string;
  showLogo: boolean;
  showCommerceName: boolean;
  showCommerceAddress: boolean;
  showClientName: boolean;
  showClientPhone: boolean;
  showPrices: boolean;
  showTotals: boolean;
  showTip: boolean;
  showDeliveryAddress: boolean;
  showDeliveryDriver: boolean;
  showComment: boolean;
  showDate: boolean;
  showLegalDisclaimer: boolean;
  extrasShowPrice: boolean;
}

/**
 * CAJA_PRESET — preset del rol 'normal' (ticket para el cliente). Imprime todo: logo,
 * datos del cliente, precios, totales, propina, domicilio, fecha y aviso legal. Es el
 * default histórico del ticket de caja; los flags coinciden con la salida byte-validada.
 * `showDeliveryDriver` es la ÚNICA excepción: nace en false (opt-in) para no alterar el
 * papel histórico — ver su doc en WebBlePrintSettings.
 */
export const CAJA_PRESET: TicketContentFlags = {
  title: 'Ticket pedido',
  showLogo: true,
  showCommerceName: true,
  showCommerceAddress: true,
  showClientName: true,
  showClientPhone: true,
  showPrices: true,
  showTotals: true,
  showTip: true,
  showDeliveryAddress: true,
  showDeliveryDriver: false,
  showComment: true,
  showDate: true,
  showLegalDisclaimer: true,
  extrasShowPrice: true,
};

/**
 * COCINA_PRESET — preset del rol 'simplified' (comanda de cocina). SIN precios/totales/
 * propina/domicilio/datos del cliente ni aviso legal: solo lo que la cocina necesita
 * (productos, instrucciones, comentario, método, mesa, fecha). Coincide con la comanda
 * simplificada byte-validada.
 */
export const COCINA_PRESET: TicketContentFlags = {
  title: 'Ticket de Cocina',
  showLogo: true,
  showCommerceName: true,
  showCommerceAddress: false,
  showClientName: false,
  showClientPhone: false,
  showPrices: false,
  showTotals: false,
  showTip: false,
  showDeliveryAddress: false,
  showDeliveryDriver: false,
  showComment: true,
  showDate: true,
  showLegalDisclaimer: false,
  extrasShowPrice: false,
};

// Devuelve el preset base (clon) del rol: 'normal' = caja → CAJA_PRESET; 'simplified' =
// cocina → COCINA_PRESET.
export const presetForRole = (ticketType: WebBleTicketType): TicketContentFlags =>
  ticketType === 'simplified' ? { ...COCINA_PRESET } : { ...CAJA_PRESET };

// Hidratación PEREZOSA de los flags de contenido desde un perfil persistido. Toma un perfil
// (que puede ser legacy: solo `ticketType` + show* viejos, sin los flags ricos) y devuelve
// los `TicketContentFlags` RESUELTOS: parte del preset del rol y aplica ENCIMA los overrides
// explícitos del perfil (campos `!== undefined`). Así un perfil legacy se comporta EXACTAMENTE
// como su preset (cero cambio de salida), y uno editado respeta los toggles que el usuario tocó.
// No muta el perfil ni reescribe la llave durable.
// showCommerceName/showCommerceAddress/showClientPhone ya existían como campos obligatorios; se
// respetan tal cual (default histórico true) salvo donde el preset cocina los deja inertes
// (address/phone no aplican a cocina por construcción del IR).
export const resolveContentFlags = (settings: WebBlePrintSettings): TicketContentFlags => {
  const preset = presetForRole(settings.ticketType);
  // override(flag) = el valor explícito del perfil si existe; si no, el del preset.
  // `K` se restringe a las llaves BOOLEANAS (excluye `title: string`) para que `preset[key]`
  // sea boolean y el helper tipe sin estrechamientos.
  type BoolFlagKey = {
    [P in keyof TicketContentFlags]: TicketContentFlags[P] extends boolean ? P : never;
  }[keyof TicketContentFlags];
  const ov = <K extends BoolFlagKey>(key: K, persisted: boolean | undefined): boolean =>
    persisted === undefined ? preset[key] : persisted;
  return {
    title: preset.title,
    showLogo: ov('showLogo', settings.showLogo),
    showCommerceName: ov('showCommerceName', settings.showCommerceName),
    showCommerceAddress: ov('showCommerceAddress', settings.showCommerceAddress),
    showClientName: ov('showClientName', settings.showClientName),
    showClientPhone: ov('showClientPhone', settings.showClientPhone),
    showPrices: ov('showPrices', settings.showPrices),
    showTotals: ov('showTotals', settings.showTotals),
    showTip: ov('showTip', settings.showTip),
    showDeliveryAddress: ov('showDeliveryAddress', settings.showDeliveryAddress),
    showDeliveryDriver: ov('showDeliveryDriver', settings.showDeliveryDriver),
    showComment: ov('showComment', settings.showComment),
    showDate: ov('showDate', settings.showDate),
    showLegalDisclaimer: ov('showLegalDisclaimer', settings.showLegalDisclaimer),
    extrasShowPrice: ov('extrasShowPrice', settings.extrasShowPrice),
  };
};

// Firma de un job de impresión BLE: identidad de la impresora física + config que define el PAPEL
// producido. Dos perfiles con la MISMA firma producen un ticket idéntico en la misma impresora →
// se colapsan en selectAutoPrintWebBlePrinters (raíz del bug de doble impresión). deviceName
// (estable) prioriza sobre deviceId (volátil entre sesiones).
export const webBlePrintJobSignature = (s: WebBlePrintSettings): string =>
  [
    s.deviceName || s.deviceId || '',
    s.ticketFormat ?? 'text',
    s.textSize,
    s.pageWidth,
    s.decoration !== false ? 1 : 0,
    JSON.stringify(resolveContentFlags(s)),
  ].join('|');

// Selección PURA de impresoras BLE para auto-print de un trigger ('all' = al recibir, 'confirmed' =
// al aceptar), DEDUPLICADA por firma de output. Colapsa entradas que producirían papel IDÉNTICO en
// la misma impresora física (dos perfiles → mismo dispositivo + misma config = doble impresión).
// Preserva el multi-ticket legítimo (caja `normal` + cocina `simplified` → firmas distintas → ambas
// sobreviven). Filtra deshabilitadas y sin dispositivo. Espeja `selectAutoPrintPrinters` de Electron
// (PR #1050) para el camino BLE.
// Qué conjuntos de impresoras BLE imprimen según el trigger del evento, evitando DOBLE impresión:
//  - 'received' (pedido normal entrante): solo 'all' ("Al recibir").
//  - 'accepted' de pedido NORMAL (el cajero tocó Aceptar): solo 'confirmed' ("Al aceptar"); las
//    'all' YA imprimieron en 'received' → incluirlas reimprimía (imprimía al recibir Y al aceptar).
//  - 'accepted' de TELÉFONO: AMBOS sets — el pedido nace aceptado y dispara una sola vez 'accepted'
//    (no hay 'received' previo). Paridad con Electron (onOrderAccepted + onOrderReceived).
export const webBleAutoPrintWanted = (trigger: 'received' | 'accepted', isTelefono: boolean): WebBleAutoPrint[] =>
  trigger === 'received' ? ['all'] : isTelefono ? ['confirmed', 'all'] : ['confirmed'];

export const selectAutoPrintWebBlePrinters = (
  printers: Record<string, WebBlePrintSettings>,
  wanted: WebBleAutoPrint[],
): WebBlePrintSettings[] => {
  const seen = new Set<string>();
  const result: WebBlePrintSettings[] = [];
  Object.keys(printers).forEach((key) => {
    const s = printers[key];
    if (!s || !s.enabled || !(s.deviceId || s.deviceName)) return;
    if (!wanted.includes(s.autoPrint)) return;
    const sig = webBlePrintJobSignature(s);
    if (seen.has(sig)) return;
    seen.add(sig);
    result.push(s);
  });
  return result;
};
